import { Button } from '@/global/components/Button'
import { FormField } from '@/global/components/Form'
import { hasPermission } from '@/hooks/useBackendMenu'
import { AppAbilities } from '@acme/auth/types'
import { ComponentProps, ReactNode } from 'react'
import { z } from 'zod'
import { Actions } from './DialogGuideActions'
import { Content } from './DialogGuideContent'
import { DialogRootProps, Root } from './DialogGuideRoot'
import { Title } from './DialogGuideTitle'

export interface DialogConfirmarProps {
    /** A pergunta do diálogo — "Tem certeza que deseja excluir o abono?". */
    title: ReactNode
    /** O que vem abaixo da pergunta: aviso, detalhes do registro, texto condicional. */
    children?: ReactNode
    onConfirm: (dados: { motivo?: string }) => void | Promise<unknown>
    open?: boolean
    onClose?: () => void
    loading?: boolean
    /** Só abre com a permissão. Entra junto com `open` quando os dois vierem. */
    permissao?: AppAbilities
    /**
     * Pede o motivo antes de confirmar. É o que o backend exige quando o
     * registro já foi aprovado, e o que vira a justificativa da solicitação
     * de exclusão feita pelo funcionário.
     */
    exigirMotivo?: boolean
    /** Desabilita os campos e o botão de confirmar. */
    desabilitado?: boolean
    /**
     * Fecha o diálogo assim que o formulário é enviado. Desligue quando o
     * próprio call site navega no sucesso da mutation — senão o `onClose`
     * padrão (`navigate(-1)`) volta uma segunda vez.
     */
    fecharAoConfirmar?: boolean
    /** Texto do botão primário. */
    confirmar?: ReactNode
    /** Texto do botão secundário. */
    cancelar?: ReactNode
    color?: ComponentProps<typeof Button>['color']
    width?: DialogRootProps['width']
}

const schemaMotivo = z.object({
    motivo: z.string().trim().min(1, 'Campo obrigatório'),
})

/**
 * Diálogo de confirmação: uma pergunta, "Cancelar" e o botão que confirma.
 * `ConfirmarExclusao` é este mesmo diálogo com os padrões da exclusão.
 */
export default function DialogConfirmar({
    title,
    children,
    onConfirm,
    open,
    onClose,
    loading,
    permissao,
    exigirMotivo,
    desabilitado,
    fecharAoConfirmar = true,
    confirmar = 'Confirmar',
    cancelar,
    color,
    width = 'fit',
}: DialogConfirmarProps) {
    const permitido = permissao ? hasPermission(permissao) : true
    const temConteudo = Boolean(children) || Boolean(exigirMotivo)

    return (
        <Root
            open={(open ?? true) && permitido}
            onClose={onClose}
            loading={loading}
            width={width}
            disableFields={desabilitado}
            schema={exigirMotivo ? schemaMotivo : undefined}
            onSubmit={({ motivo }) => onConfirm({ motivo })}
        >
            <Title wrap>{title}</Title>
            {temConteudo && (
                <Content>
                    {children}
                    {exigirMotivo && (
                        <FormField
                            name="motivo"
                            label="Motivo"
                            required
                            multiline
                            max={150}
                        />
                    )}
                </Content>
            )}
            <Actions>
                <Actions.SecondaryButton>{cancelar}</Actions.SecondaryButton>
                <Actions.PrimaryButton
                    color={color}
                    // `disabled` só entra quando desabilita de fato: o
                    // `PrimaryButton` espalha as props por cima do próprio
                    // `disabled={loading || props.disabled}`, então mandar
                    // `undefined` destravaria o botão durante o loading.
                    {...(desabilitado && { disabled: true })}
                    closeOnSubmit={fecharAoConfirmar}
                >
                    {confirmar}
                </Actions.PrimaryButton>
            </Actions>
        </Root>
    )
}
